import { useRef, useState, DragEvent } from 'react';
import { ArrowLeftIcon, ArrowUpIcon, XIcon } from 'lucide-react';
import { Mission } from '../../data/missions';
import HowToUse from '../HowToUse/HowToUse';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { mergeExcelFiles, MergeOptions } from '../../api/excelMergerApi';
import { useFileRun } from '../../hooks/useFileRun';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the P&L workbook (required) — this is the base file the merge is built around.',
  'Optionally add any other workbooks that should be merged into it.',
  'Adjust merge options if needed, then hit Merge Files.',
  'The merged workbook downloads automatically once it is ready.',
];

const TOGGLE_OPTIONS = [
  ['copy_images', 'Copy images'],
  ['preserve_formulas', 'Preserve formulas'],
  ['add_sumifs', 'Add SUMIFS'],
  ['create_reference_sheet', 'Create reference sheet'],
] as const;

type ToggleKey = (typeof TOGGLE_OPTIONS)[number][0];

interface ExcelMergerPanelProps {
  mission: Mission;
  onBack: () => void;
}

type FullOptions = Required<MergeOptions>;

const DEFAULT_OPTIONS: FullOptions = {
  copy_images: true,
  preserve_formulas: true,
  add_sumifs: true,
  create_reference_sheet: true,
  output_filename: 'Merged_Excel_Files.xlsx',
};

function ExcelMergerPanel({ mission, onBack }: ExcelMergerPanelProps) {
  const [plFile, setPlFile] = useState<File | null>(null);
  const [otherFiles, setOtherFiles] = useState<File[]>([]);
  const [options, setOptions] = useState<FullOptions>(DEFAULT_OPTIONS);
  const { status, error, result, fail, run } = useFileRun<Awaited<ReturnType<typeof mergeExcelFiles>>>();

  const plInputRef = useRef<HTMLInputElement>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);
  const [plDragActive, setPlDragActive] = useState(false);
  const [otherDragActive, setOtherDragActive] = useState(false);

  const addOtherFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Snapshot now: the live FileList is emptied when the input is reset.
    const picked = Array.from(files);
    setOtherFiles((prev) => [...prev, ...picked]);
  };

  const removeOtherFile = (index: number) => {
    setOtherFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleOption = (key: ToggleKey) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSubmit = async () => {
    if (!plFile) {
      fail('Upload the P&L workbook before merging.');
      return;
    }
    await run(() => mergeExcelFiles(plFile, otherFiles, options), 'Merge failed.');
  };

  return (
    <section
      style={{ borderTopColor: mission.color }}
      className="mx-auto max-w-[700px] rounded-2xl border border-border border-t-[6px] bg-card p-8 shadow-sm"
    >
      <Button
        variant="ghost"
        onClick={onBack}
        style={{ color: mission.color }}
        className="mb-5 h-auto gap-1.5 px-2 py-1.5 text-sm font-semibold hover:bg-transparent"
      >
        <ArrowLeftIcon className="size-5" /> All missions
      </Button>

      <div className="mb-6 flex items-start gap-4">
        <span
          style={{ backgroundColor: `${mission.color}26`, color: mission.color }}
          className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-xl"
        >
          <mission.icon className="h-7 w-7" />
        </span>
        <div>
          <h2 className="mb-1 text-xl font-bold text-foreground">{mission.title}</h2>
          <p className="text-sm text-muted-foreground">{mission.description}</p>
        </div>
      </div>

      <HowToUse steps={STEPS} />

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">P&L workbook (required)</Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setPlDragActive(true);
          }}
          onDragLeave={() => setPlDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setPlDragActive(false);
            const dropped = e.dataTransfer.files && e.dataTransfer.files[0];
            if (dropped) setPlFile(dropped);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            plDragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={plDragActive ? { borderColor: mission.color } : undefined}
        >
          <Button
            onClick={() => plInputRef.current?.click()}
            style={{ backgroundColor: mission.color }}
            className="text-white hover:opacity-90"
          >
            <ArrowUpIcon /> Upload P&L file
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop a file here</span>
          <span className="text-xs text-muted-foreground">{mission.maxSizeLabel}</span>
          <input
            ref={plInputRef}
            type="file"
            accept=".xlsx"
            hidden
            onChange={(e) => {
              const picked = e.target.files && e.target.files[0];
              if (picked) setPlFile(picked);
              e.target.value = '';
            }}
          />
        </div>
        {plFile && (
          <div className="mt-2 flex items-center justify-between rounded-lg bg-green-50 px-4 py-2.5 text-sm text-green-700">
            <span>📄 {plFile.name}</span>
            <button
              type="button"
              onClick={() => setPlFile(null)}
              className="text-green-700 hover:text-green-900"
              aria-label="Remove P&L file"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        )}
      </div>

      <div className="mb-5">
        <Label className="mb-2 text-sm font-semibold text-foreground">Other workbooks (optional)</Label>
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setOtherDragActive(true);
          }}
          onDragLeave={() => setOtherDragActive(false)}
          onDrop={(e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            setOtherDragActive(false);
            addOtherFiles(e.dataTransfer.files);
          }}
          className={`flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors ${
            otherDragActive ? 'border-current bg-muted' : 'border-input bg-muted'
          }`}
          style={otherDragActive ? { borderColor: mission.color } : undefined}
        >
          <Button onClick={() => otherInputRef.current?.click()} variant="outline">
            <ArrowUpIcon /> Add workbooks
          </Button>
          <span className="text-sm text-muted-foreground">or drag and drop files here — you can add more than one</span>
          <input
            ref={otherInputRef}
            type="file"
            accept=".xlsx"
            multiple
            hidden
            onChange={(e) => {
              addOtherFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
        {otherFiles.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1.5">
            {otherFiles.map((file, i) => (
              <li
                key={`${file.name}-${i}`}
                className="flex items-center justify-between rounded-lg bg-muted px-4 py-2 text-sm text-muted-foreground"
              >
                <span>📄 {file.name}</span>
                <button
                  type="button"
                  onClick={() => removeOtherFile(i)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${file.name}`}
                >
                  <XIcon className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <p className="mb-3 text-sm font-semibold text-foreground">Merge options</p>
        <div className="mb-4 flex flex-col gap-2">
          {TOGGLE_OPTIONS.map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={options[key]}
                onChange={() => toggleOption(key)}
                className="size-4 rounded border-input"
                style={{ accentColor: mission.color }}
              />
              {label}
            </label>
          ))}
        </div>
        <Label htmlFor="output-filename" className="mb-1.5">
          Output filename
        </Label>
        <Input
          id="output-filename"
          value={options.output_filename}
          onChange={(e) => setOptions((prev) => ({ ...prev, output_filename: e.target.value }))}
          placeholder="Merged_Excel_Files.xlsx"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Merging…' : 'Merge Files'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Merging…'} />}

      {status === 'error' && error && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">⚠️ {error}</div>
      )}
      {status === 'success' && result && (
        <div className="mt-4 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-700">
          ✅ {result.filename} downloaded
          {result.sheetCount !== null ? ` — ${result.sheetCount} sheets` : ''}.
        </div>
      )}
      {status === 'idle' && (
        <div className="mt-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700">
          📁 Upload a P&L workbook above to get started.
        </div>
      )}
    </section>
  );
}

export default ExcelMergerPanel;
